import React, { useContext, useEffect, useState } from 'react';
import { CircularProgress } from '@material-ui/core';

import { fbmApi as api } from 'api/fbm';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications } from 'shared';

function resolveLotDetailsRequestParams(row) {
    const facilityId = row.facilityID ?? row.facilityId;
    const type = (row.type || '').toLowerCase();
    const externalSku = type === 'item' ? (row.externalSku ?? row.id ?? null) : null;
    const packagingId = type === 'packaging' ? (row.packagingId ?? row.packagingID ?? row.id ?? null) : null;

    return { facilityId, externalSku, packagingId };
}

function FBMItemLotDetails({ parentRow }) {
    const { fbmAccount } = useContext(UserContext);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [loading, setLoading] = useState(true);
    const [lotDetails, setLotDetails] = useState([]);

    const { externalSku, facilityID, facilityId, id: rowId, packagingId, packagingID, type } = parentRow.original;

    useEffect(() => {
        let cancelled = false;

        const loadLotDetails = async () => {
            const params = resolveLotDetailsRequestParams({
                id: rowId,
                type,
                facilityID,
                facilityId,
                externalSku,
                packagingId,
                packagingID,
            });

            if (!params.facilityId || (!params.externalSku && !params.packagingId)) {
                setNotification({
                    text: 'Unable to load lot details for this row (missing facility or sku/packaging id).',
                    level: 'warning',
                });
                setLotDetails([]);
                setLoading(false);
                return;
            }

            setLoading(true);
            try {
                const response = await api.getInventoryLotDetailsV2({
                    facilityId: params.facilityId,
                    fbmAccount: fbmAccount.fbmAccountName,
                    externalSku: params.externalSku,
                    packagingId: params.packagingId,
                });

                if (cancelled) {
                    return;
                }

                if (!response.status) {
                    setNotification({
                        text: `Failed to get lot details: ${response.msg}`,
                        level: 'error',
                    });
                    setLotDetails([]);
                    return;
                }

                setLotDetails(response.data?.lotDetails ?? []);
            } catch (error) {
                if (cancelled) {
                    return;
                }
                setNotification({
                    text: `Failed to get lot details: ${error.message}`,
                    level: 'error',
                });
                setLotDetails([]);
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadLotDetails();

        return () => {
            cancelled = true;
        };
    }, [externalSku, facilityID, facilityId, fbmAccount.fbmAccountName, packagingId, packagingID, rowId, type]);

    const cols = [
        { Header: 'Lot Number', accessor: 'lotID' },
        { Header: 'Partner Lot', accessor: 'partnerLotNumber' },
        { Header: 'Received On', accessor: 'receivedAt' },
        { Header: 'Expires On', accessor: 'expiresOn' },
        { Header: 'Num Units', accessor: 'numUnits' },
        { Header: 'Status', accessor: 'status' },
    ];

    return (
        <div style={{ backgroundColor: '#f4f5fb' }}>
            <Notifications options={notification} />
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                    <CircularProgress size={32} />
                </div>
            ) : (
                <DataTable
                    columns={cols}
                    data={lotDetails}
                    initialSortBy={[
                        {
                            id: 'numUnits',
                            desc: true,
                        },
                    ]}
                />
            )}
        </div>
    );
}

export { FBMItemLotDetails };
